/**
 * @license
 * Copyright 2025 Steven Roussey <sroussey@gmail.com>
 * SPDX-License-Identifier: Apache-2.0
 */

import {
  createServiceToken,
  deepEqual,
  getLogger,
  makeFingerprint,
  sleep,
  uuid4,
} from "@workglow/util";
import { DataPortSchemaObject, FromSchema, TypedArraySchemaOptions } from "@workglow/util/schema";
import { mkdir, readdir, readFile, rm, writeFile } from "node:fs/promises";
import path from "node:path";
import { type ITabularMigration, type ITabularMigrationApplier } from "../migrations";
import { PollingSubscriptionManager } from "../util/PollingSubscriptionManager";
import {
  BaseTabularStorage,
  ClientProvidedKeysOption,
  KeyGenerationStrategy,
} from "./BaseTabularStorage";
import { InMemoryTabularMigrationApplier } from "./InMemoryTabularMigrationApplier";
import {
  AnyTabularStorage,
  AutoGeneratedKeys,
  CoveringIndexQueryOptions,
  DeleteSearchCriteria,
  InsertEntity,
  QueryOptions,
  SearchCriteria,
  SimplifyPrimaryKey,
  TabularChangePayload,
  TabularSubscribeOptions,
} from "./ITabularStorage";
import { StorageUnsupportedError } from "./StorageError";

export const FS_FOLDER_TABULAR_REPOSITORY = createServiceToken<AnyTabularStorage>(
  "storage.tabularRepository.fsFolder"
);

class FsFolderMigrationApplier extends InMemoryTabularMigrationApplier {
  private loaded = false;
  constructor(
    storage: AnyTabularStorage,
    private readonly folderPath: string
  ) {
    super(storage, "fsfolder");
  }
  override async ensureBookkeeping(): Promise<void> {
    await this.load();
  }
  override async appliedVersions(component: string): Promise<Set<number>> {
    await this.load();
    return new Set(this.applied.get(component) ?? []);
  }
  private async load(): Promise<void> {
    if (this.loaded) return;
    const file = `${this.folderPath}/_storage_migrations.json`;
    try {
      const text = await readFile(file, "utf8");
      const parsed = JSON.parse(text) as Record<string, number[]>;
      for (const [c, vs] of Object.entries(parsed)) {
        this.applied.set(c, new Set(vs));
      }
    } catch (err: unknown) {
      if ((err as { code?: string })?.code !== "ENOENT") throw err;
    }
    this.loaded = true;
  }
  protected override async persist(): Promise<void> {
    const file = `${this.folderPath}/_storage_migrations.json`;
    const out: Record<string, number[]> = {};
    for (const [c, vs] of this.applied) out[c] = [...vs].sort((a, b) => a - b);
    await writeFile(file, JSON.stringify(out, null, 2));
  }
}

/** Tabular repository where each row is stored as a JSON file in a directory. */
export class FsFolderTabularStorage<
  Schema extends DataPortSchemaObject,
  PrimaryKeyNames extends ReadonlyArray<keyof Schema["properties"]>,
  // computed types
  Entity = FromSchema<Schema, TypedArraySchemaOptions>,
  PrimaryKey = SimplifyPrimaryKey<Entity, PrimaryKeyNames>,
  Value = Omit<Entity, PrimaryKeyNames[number] & keyof Entity>,
  InsertType = InsertEntity<Entity, AutoGeneratedKeys<Schema>>,
> extends BaseTabularStorage<Schema, PrimaryKeyNames, Entity, PrimaryKey, Value, InsertType> {
  private folderPath: string;
  private autoIncrementCounter = 0;
  private pollingManager: PollingSubscriptionManager<
    Entity,
    string,
    TabularChangePayload<Entity>
  > | null = null;

  /** `indexes` is accepted for shape compatibility but is unused here. */
  constructor(
    folderPath: string,
    schema: Schema,
    primaryKeyNames: PrimaryKeyNames,
    indexes: readonly (keyof NoInfer<Entity> | readonly (keyof NoInfer<Entity>)[])[] = [],
    clientProvidedKeys: ClientProvidedKeysOption = "if-missing",
    tabularMigrations?: ReadonlyArray<ITabularMigration>
  ) {
    super(schema, primaryKeyNames, indexes, clientProvidedKeys, tabularMigrations, "fsfolder");
    this.folderPath = path.join(folderPath);
  }

  async setupDirectory(): Promise<void> {
    try {
      await mkdir(this.folderPath, { recursive: true });
    } catch (error) {
      // CI system sometimes has issues temporarily; retry once.
      await new Promise((resolve) => setTimeout(resolve, 0));
      try {
        await mkdir(this.folderPath, { recursive: true });
      } catch {
        // Ignore: directory likely already exists.
      }
    }
  }

  override async setupDatabase(): Promise<void> {
    await this.setupDirectory();
    if (this.tabularMigrations && this.tabularMigrations.length > 0) {
      await this.applyTabularMigrations();
    }
  }

  public override getMigrationApplier(): ITabularMigrationApplier | null {
    return new FsFolderMigrationApplier(this as unknown as AnyTabularStorage, this.folderPath);
  }

  protected override generateKeyValue(
    columnName: string,
    strategy: KeyGenerationStrategy
  ): string | number {
    if (strategy === "autoincrement") {
      return ++this.autoIncrementCounter;
    } else {
      return uuid4();
    }
  }

  async put(entity: InsertType): Promise<Entity> {
    let entityToStore = entity as unknown as Entity;

    if (this.hasAutoGeneratedKey() && this.autoGeneratedKeyName) {
      const keyName = this.autoGeneratedKeyName as string;
      const clientProvidedValue = (entity as Record<string, unknown>)[keyName];
      const hasClientValue = clientProvidedValue !== undefined && clientProvidedValue !== null;

      let shouldGenerate = false;
      if (this.clientProvidedKeys === "never") {
        shouldGenerate = true;
      } else if (this.clientProvidedKeys === "always") {
        if (!hasClientValue) {
          throw new Error(
            `Auto-generated key "${keyName}" is required when clientProvidedKeys is "always"`
          );
        }
        shouldGenerate = false;
      } else {
        shouldGenerate = !hasClientValue;
      }

      if (shouldGenerate) {
        const generatedValue = this.generateKeyValue(keyName, this.autoGeneratedKeyStrategy!);
        entityToStore = { ...entity, [keyName]: generatedValue } as Entity;
      }
    }

    await this.setupDirectory();
    const filePath = await this.getFilePath(entityToStore);
    try {
      await writeFile(filePath, JSON.stringify(entityToStore));
    } catch (error) {
      // CI system sometimes has issues temporarily; retry once.
      await sleep(1);
      try {
        await writeFile(filePath, JSON.stringify(entityToStore));
      } catch (retryError) {
        throw new Error(
          `Failed to write file "${filePath}" after retry: ${retryError instanceof Error ? retryError.message : String(retryError)}`,
          { cause: retryError }
        );
      }
    }
    this.events.emit("put", entityToStore);
    return entityToStore;
  }

  async putBulk(entities: InsertType[]): Promise<Entity[]> {
    await this.setupDirectory();
    return await Promise.all(entities.map(async (entity) => this.put(entity)));
  }

  async get(key: PrimaryKey): Promise<Entity | undefined> {
    await this.setupDirectory();
    const filePath = await this.getFilePath(key);
    try {
      const buf = await readFile(filePath);
      const data = buf.toString("utf8");
      const entity = JSON.parse(data) as Entity;
      this.events.emit("get", key, entity);
      return entity;
    } catch (error) {
      this.events.emit("get", key, undefined);
      return undefined;
    }
  }

  async delete(value: PrimaryKey | Entity): Promise<void> {
    await this.setupDirectory();
    const { key } = this.separateKeyValueFromCombined(value as Entity);
    const filePath = await this.getFilePath(key);
    try {
      await rm(filePath);
    } catch (error) {
      console.error("Error deleting file", filePath, error);
    }
    this.events.emit("delete", key as Partial<Entity>);
  }

  async getAll(): Promise<Entity[] | undefined> {
    await this.setupDirectory();
    try {
      const files = await readdir(this.folderPath);
      // Exclude internal bookkeeping files (prefixed with "_").
      const jsonFiles = files.filter((file) => file.endsWith(".json") && !file.startsWith("_"));
      if (jsonFiles.length === 0) {
        return undefined;
      }
      const results = await Promise.allSettled(
        jsonFiles.map(async (file) => {
          const buf = await readFile(path.join(this.folderPath, file));
          const content = buf.toString("utf8");
          const data = JSON.parse(content) as Entity;
          return data;
        })
      );

      const values = results
        .filter((result) => result.status === "fulfilled")
        .map((result) => result.value);

      return values.length > 0 ? values : undefined;
    } catch (error) {
      console.error("Error in getAll:", error);
      throw error;
    }
  }

  async deleteAll(): Promise<void> {
    await this.setupDirectory();
    try {
      await rm(this.folderPath, { recursive: true, force: true });
    } catch (error) {
      console.error("Error deleting folder", this.folderPath, error);
      await rm(this.folderPath, { recursive: true, force: true });
    }
    this.events.emit("clearall");
  }

  async size(): Promise<number> {
    await this.setupDirectory();
    // Count data files only; exclude internal bookkeeping files (prefixed with "_").
    const files = await readdir(this.folderPath);
    const jsonFiles = files.filter((file) => file.endsWith(".json") && !file.startsWith("_"));
    return jsonFiles.length;
  }

  async getOffsetPage(offset: number, limit: number): Promise<Entity[] | undefined> {
    await this.setupDirectory();
    const files = await readdir(this.folderPath);
    // Exclude internal bookkeeping files (prefixed with "_").
    const jsonFiles = files.filter((file) => file.endsWith(".json") && !file.startsWith("_"));

    if (jsonFiles.length === 0) {
      return undefined;
    }

    const results = await Promise.allSettled(
      jsonFiles.map(async (file) => {
        const filePath = path.join(this.folderPath, file);
        try {
          const content = await readFile(filePath, "utf8");
          return JSON.parse(content) as Entity;
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          throw new Error(`Failed to read or parse "${filePath}": ${message}`);
        }
      })
    );
    const allEntities: Entity[] = [];
    for (const result of results) {
      if (result.status === "fulfilled") {
        allEntities.push(result.value);
      } else {
        getLogger().warn(
          `Skipping corrupted file in getOffsetPage: ${result.reason?.message ?? result.reason}`
        );
      }
    }

    // Sort by primary key for deterministic ordering. O(n log n) on every
    // call — acceptable because this backend is for small datasets / tests.
    allEntities.sort((a, b) => {
      for (const key of this.primaryKeyNames) {
        const aVal = (a as Record<string, string | number>)[key as string];
        const bVal = (b as Record<string, string | number>)[key as string];
        if (aVal < bVal) return -1;
        if (aVal > bVal) return 1;
      }
      return 0;
    });

    const page = allEntities.slice(offset, offset + limit);
    return page.length > 0 ? page : undefined;
  }

  private async getFilePath(value: PrimaryKey | Entity): Promise<string> {
    const { key } = this.separateKeyValueFromCombined(value as Entity);
    const filename = await this.getKeyAsIdString(key);
    const fullPath = path.join(this.folderPath, `${filename}.json`);
    return fullPath;
  }

  async query(
    _criteria: SearchCriteria<Entity>,
    _options?: QueryOptions<Entity>
  ): Promise<Entity[] | undefined> {
    throw new StorageUnsupportedError("query", "FsFolderTabularStorage");
  }

  override async queryIndex<K extends keyof Entity & string>(
    _criteria: SearchCriteria<Entity>,
    _options: CoveringIndexQueryOptions<Entity, K>
  ): Promise<Pick<Entity, K>[]> {
    throw new StorageUnsupportedError("queryIndex", "FsFolderTabularStorage");
  }

  async deleteSearch(_criteria: DeleteSearchCriteria<Entity>): Promise<void> {
    throw new Error("deleteSearch is not supported for FsFolderTabularStorage");
  }

  /** Lazy-init so all subscriptions share a single polling loop per interval. */
  private getPollingManager(): PollingSubscriptionManager<
    Entity,
    string,
    TabularChangePayload<Entity>
  > {
    if (!this.pollingManager) {
      this.pollingManager = new PollingSubscriptionManager<
        Entity,
        string,
        TabularChangePayload<Entity>
      >(
        async () => {
          const entities = (await this.getAll()) || [];
          const map = new Map<string, Entity>();
          for (const entity of entities) {
            const { key } = this.separateKeyValueFromCombined(entity);
            const fingerprint = await makeFingerprint(key);
            map.set(fingerprint, entity);
          }
          return map;
        },
        (a, b) => deepEqual(a, b),
        {
          insert: (item) => ({ type: "INSERT" as const, new: item }),
          update: (oldItem, newItem) => ({ type: "UPDATE" as const, old: oldItem, new: newItem }),
          delete: (item) => ({ type: "DELETE" as const, old: item }),
        }
      );
    }
    return this.pollingManager;
  }

  /** Filesystem has no native change notifications; we poll. */
  override subscribeToChanges(
    callback: (change: TabularChangePayload<Entity>) => void,
    options?: TabularSubscribeOptions
  ): () => void {
    // Don't await setupDirectory() — keeps the method sync. The polling
    // manager calls getAll(), which sets up the directory when needed.
    const intervalMs = options?.pollingIntervalMs ?? 1000;
    const manager = this.getPollingManager();
    return manager.subscribe(callback, { intervalMs });
  }

  override destroy(): void {
    if (this.pollingManager) {
      this.pollingManager.destroy();
      this.pollingManager = null;
    }
    super.destroy();
  }
}
